import { Controller } from "@hotwired/stimulus"
import { Z_INDEX_LAYERS, Z_INDEX_STEP } from "~/constants/z_index"
import { DialogStackManager } from "~/dialog/stack_manager"
import { DialogCleanupService } from "~/dialog/cleanup_service"
import { DialogEventHandler } from "~/dialog/event_handler"
import { StateMachine } from "~/dialog/state_machine"
import { DialogDOMUtils } from "~/dialog/dom_utils"
import { createLogger } from "~/utils/logger"

const log = createLogger("Dialog")

let openCount = 0

const CONSTANTS = {
  ANIMATION_DURATION: 200
}

export default class extends Controller {
  static targets = ["content"]
  static values = {
    open: {
      type: Boolean,
      default: false
    }
  }

  connect() {
    this.insertedDialogEventCleanup = []

    // Instantiate services
    this.stackManager = new DialogStackManager()
    this.cleanupService = new DialogCleanupService()
    this.eventHandler = new DialogEventHandler(this.element)

    // State machine: CLOSED → OPENING → OPEN → CLOSING
    this.fsm = new StateMachine('CLOSED', {
      CLOSED: ['OPENING'],
      OPENING: ['OPEN', 'CLOSING'],
      OPEN: ['CLOSING'],
      CLOSING: ['CLOSED', 'OPENING']
    })

    // Bind and register global event handlers
    this._setupGlobalListeners()

    if (this.openValue) this.open()

    // Register root modal if already present/open
    if (!this.hasContentTarget && this._hasOverlayStructure(this.element)) {
      this._registerRootModal()
    }
  }

  _setupGlobalListeners() {
    // Global listeners via EventHandler (includes document-level turbo:submit-end)
    this.eventHandler.registerGlobal({
      onEscape: (e) => this.handleKeydown(e),
      onWindowDismiss: () => this.dismiss(),
      onGlobalSubmitEnd: (e) => this.handleFormSubmission(e)
    })

    // If this is a root modal already present/open in the DOM, register it in the stack
    if (!this.hasContentTarget && this._hasOverlayStructure(this.element)) {
      if (!this.stackManager.includes(this.element)) this.stackManager.push(this.element)
      this.openValue = true
      this.preventBodyScroll()
      this.refreshLayers()
      this._ensureRootBackdropClickListener()
    }
  }

  _registerRootModal() {
    this.stackManager.push(this.element)
    this.openValue = true
    this.preventBodyScroll()
    this.refreshLayers()
    this._ensureRootBackdropClickListener()
  }

  disconnect() {
    this._cleanupGlobalListeners()
    this.cleanupInsertedDialogEvents()
    this._performDefensiveCleanup()
    this.allowBodyScroll()
  }

  _cleanupGlobalListeners() {
    try { this.eventHandler && this.eventHandler.unregisterGlobal() } catch (_) {}
  }

  _performDefensiveCleanup() {
    try {
      this._cleanupInsertedDialog()
      this.stackManager.remove(this.element)
      this.refreshLayers()
      this.purgeOrphanOverlays()
    } catch (e) {
      log.warn('Dialog disconnect cleanup failed:', e)
    }
  }

  _cleanupInsertedDialog() {
    if (this.insertedDialog && document.contains(this.insertedDialog)) {
      this.disablePointerEvents(this.insertedDialog)
      this.stackManager.remove(this.insertedDialog)
      this.insertedDialog.remove()
    }
    this.insertedDialog = null
  }

  open(event) {
    if (event) event.preventDefault()
    // Use FSM to guard transitions
    const onEnter = (_, token) => this._onEnterOpening(token)
    if (this.fsm.state === 'OPEN') return
    this.fsm.transitionTo('OPENING', { onEnter })
  }

  _onEnterOpening(token) {
    if (this.hasContentTarget) {
      let appendedRoot = null
      const tpl = this.contentTarget

      if (tpl && tpl.tagName === 'TEMPLATE' && tpl.content) {
        const fragment = tpl.content.cloneNode(true)
        document.body.appendChild(fragment)
        appendedRoot = document.body.lastElementChild
      } else if (this.contentTarget.firstElementChild) {
        const clone = this.contentTarget.firstElementChild.cloneNode(true)
        document.body.appendChild(clone)
        appendedRoot = clone
      }

      if (appendedRoot) {
        this.insertedDialog = appendedRoot
        // Local events for inserted dialog
        try {
          this.eventHandler.unregisterLocal()
          this.eventHandler.registerLocalFor(this.insertedDialog, {
            onBackdropClick: () => this.dismiss(),
            onCloseButtonClick: () => this.dismiss(),
            onSubmitEnd: (e) => this.handleFormSubmission(e)
          })
        } catch (_) {}

        this.stackManager.push(this.insertedDialog)
        this.refreshLayers()
      }
    } else {
      this.stackManager.push(this.element)
      this.refreshLayers()
      this._ensureRootBackdropClickListener()
    }

    this.preventBodyScroll()
    this.openValue = true
    this.focusDialog()

    // Transition to OPEN on next tick if token still matches
    setTimeout(() => {
      if (token === this.fsm.token) this.fsm.transitionTo('OPEN')
    }, 0)
  }

  dismiss(event) {
    if (event) event.preventDefault()
    const onEnter = (_, token) => this._onEnterClosing(token)
    this.fsm.transitionTo('CLOSING', { onEnter })
  }

  _onEnterClosing(token) {
    this.openValue = false

    const dialogEl = this.findDialogToClose()
    if (!dialogEl) {
      this.allowBodyScroll()
      return
    }

    this.markDialogAsClosed(dialogEl)
    this.disablePointerEvents(dialogEl)
    this.removeFromStackAndRefresh(dialogEl)
    this.setClosedStateOnElements(dialogEl)

    this.cleanupService.scheduleCleanup(dialogEl, {
      animationDuration: CONSTANTS.ANIMATION_DURATION,
      onCleanupComplete: (el) => {
        if (token !== this.fsm.token) return
        try { this.cleanupDialog(el) } catch (error) { log.warn('cleanupDialog error:', error) }
        this.allowBodyScroll()
        this.fsm.transitionTo('CLOSED')
      }
    })
    try { this.purgeOrphanOverlays() } catch (_) {}
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      const root = this.insertedDialog || this.element
      if (!root) return
      if (this.isTopmost(root) || (this.stackManager.length() === 0 && this.openValue)) {
        event.preventDefault()
        this.dismiss()
      }
    }
  }

  preventBodyScroll() {
    // Use atomic increment to prevent race conditions
    const newCount = ++openCount
    if (newCount === 1) {
      document.body.classList.add('overflow-hidden')
    }
  }

  allowBodyScroll() {
    // Use atomic decrement to prevent race conditions
    if (openCount > 0) {
      const newCount = --openCount
      if (newCount === 0) {
        document.body.classList.remove('overflow-hidden')
      }
    }
  }

  focusDialog() {
    const dialogElement = this.insertedDialog || this.element
    const focusableElements = DialogDOMUtils.getFocusableElements(dialogElement)

    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }
  }

  setupInsertedDialogEvents() {
    if (!this.insertedDialog) return
    try {
      this.eventHandler.unregisterLocal()
      this.eventHandler.registerLocalFor(this.insertedDialog, {
        onBackdropClick: () => this.dismiss(),
        onCloseButtonClick: () => this.dismiss(),
        onSubmitEnd: (e) => this.handleFormSubmission(e)
      })
      // Track for compatibility with previous cleanup mechanism
      this.insertedDialogEventCleanup.push(() => {
        try { this.eventHandler.unregisterLocal() } catch (_) {}
      })
    } catch (e) {
      log.warn('setupInsertedDialogEvents error:', e)
    }
  }

  _hasOverlayStructure(el) {
    try {
      if (!el) return false
      if (el.getAttribute && el.getAttribute('role') === 'dialog') return true
      return DialogDOMUtils.hasOverlayStructure(el)
    } catch (_) {
      return false
    }
  }

  _ensureRootBackdropClickListener() {
    try {
      const root = this.element
      const backdrop = root && root.querySelector('div[class*="fixed"][class*="inset-0"]')
      if (!backdrop) return
      const handler = (event) => {
        if (event.target === backdrop) this.dismiss()
      }
      backdrop.addEventListener('click', handler)
      // Track cleanup with the same mechanism we use for inserted dialogs
      this.insertedDialogEventCleanup.push(() => {
        try { backdrop.removeEventListener('click', handler) } catch (_) {}
      })
    } catch (_) { /* noop */ }
  }

  refreshLayers() {
    // Prune stale
    this.stackManager.cleanupStale({
      isClosedFn: (el) => this._isDialogClosed(el),
      onDisablePointerEvents: (el) => this.disablePointerEvents(el)
    })
    // Apply layering
    this.stackManager.applyLayering({
      styleBackdrop: (backdrop, layerIndex, index, isTop) => this._styleBackdrop(backdrop, layerIndex, index, isTop),
      stylePanel: (panel, layerIndex, isTop) => this._stylePanel(panel, layerIndex, isTop),
      styleOtherFixedNodes: (fixedNodes, backdrop, panel, isTop) => this._styleOtherNodes(fixedNodes, backdrop, panel, isTop)
    })
    // Purge orphans
    try {
      this.cleanupService.purgeOrphanOverlays({
        animationDuration: CONSTANTS.ANIMATION_DURATION,
        onDisablePointerEvents: (el) => this.disablePointerEvents(el),
        onRefreshLayers: () => this.refreshLayers(),
        isActiveRootFn: (root, inStack) => {
          if (DialogDOMUtils.hasActiveRecipeForm(root)) return true
          const { fixedNodes, hasBackdrop } = DialogDOMUtils.getDialogStructure(root)
          const allClosedOrGone = fixedNodes.length === 0 || fixedNodes.every(n => {
            const state = n.getAttribute('data-state')
            const display = (n.style && n.style.display) || ''
            return state === 'closed' || display === 'none'
          })
          const rootClosed = root.getAttribute('data-state') === 'closed'
          return inStack.has(root) && !(rootClosed || allClosedOrGone)
        }
      })
    } catch (_) {}
  }

  _cleanupStaleDialogs() {
    // Backward compatibility shim (used by some flows); delegate to stackManager
    this.stackManager.cleanupStale({
      isClosedFn: (el) => this._isDialogClosed(el),
      onDisablePointerEvents: (el) => this.disablePointerEvents(el)
    })
  }

  _isDialogClosed(el) {
    const fixedNodes = Array.from(el.querySelectorAll('div[class*="fixed"]'))
    const allClosedOrGone = fixedNodes.length === 0 || fixedNodes.every(n => {
      const state = n.getAttribute('data-state')
      const display = (n.style && n.style.display) || ''
      return state === 'closed' || display === 'none'
    })
    return el.getAttribute('data-state') === 'closed' || allClosedOrGone
  }

  _applyLayering() {
    // Backward compatibility shim; delegate to stackManager
    this.stackManager.applyLayering({
      styleBackdrop: (backdrop, layerIndex, index, isTop) => this._styleBackdrop(backdrop, layerIndex, index, isTop),
      stylePanel: (panel, layerIndex, isTop) => this._stylePanel(panel, layerIndex, isTop),
      styleOtherFixedNodes: (fixedNodes, backdrop, panel, isTop) => this._styleOtherNodes(fixedNodes, backdrop, panel, isTop)
    })
  }

  _styleBackdrop(backdrop, layerIndex, index, isTop) {
    if (!backdrop) return
    backdrop.style.zIndex = String(Z_INDEX_LAYERS.DIALOG_BASE * layerIndex)
    backdrop.style.pointerEvents = isTop ? 'auto' : 'none'
    backdrop.style.background = index === 0 ? '' : 'transparent'
  }

  _stylePanel(panel, layerIndex, isTop) {
    if (!panel) return
    panel.style.zIndex = String(Z_INDEX_LAYERS.DIALOG_BASE * layerIndex + Z_INDEX_STEP)
    panel.style.pointerEvents = isTop ? 'auto' : 'none'
  }

  _styleOtherNodes(fixedNodes, backdrop, panel, isTop) {
    fixedNodes.forEach(node => {
      if (node !== panel && node !== backdrop) {
        node.style.pointerEvents = isTop ? 'auto' : 'none'
      }
    })
    try { this.purgeOrphanOverlays() } catch (_) {}
  }

  handleFormSubmission(event) {
    try {
      const formEl = event?.target
      if (!formEl) return

      // Only handle forms within this dialog instance
      const dialogEl = this.insertedDialog || this.element
      if (!dialogEl || !dialogEl.contains(formEl)) return

      const success = event?.detail ? (event.detail.success !== false && !event.detail.error) : true
      if (!success) return

      // Clean up dialog roots that contain this form
      // This covers Turbo morph timing where the form may have moved out of our root between submit and submit-end
      try {
        this.cleanupAllDialogsForElement(formEl)
      } catch (e) {
        log.warn('Dialog post-submit cleanup error:', e)
      }
      try { this.purgeOrphanOverlays() } catch (_) {}
    } catch (e) {
      log.warn('handleFormSubmission error:', e)
    }
  }

  cleanupInsertedDialogEvents() {
    // Execute all cleanup functions and clear the array
    this.insertedDialogEventCleanup.forEach(cleanup => {
      try {
        cleanup()
      } catch (error) {
        log.warn('Error during dialog event cleanup:', error)
      }
    })
    this.insertedDialogEventCleanup = []
  }

  findDialogToClose() {
    let dialogEl = this.insertedDialog || this.element

    // If this instance has a content target but no insertedDialog (e.g. re-connected)
    // and our element is gone/not in the DOM, fall back to the top-most dialog
    const needsFallback = !this.insertedDialog && this.hasContentTarget
    if (needsFallback && (!dialogEl || !document.contains(dialogEl)) && this.stackManager.length() > 0) {
      dialogEl = this.stackManager.top()
    }

    return dialogEl
  }

  isTopmost(el = (this.insertedDialog || this.element)) {
    if (!el) return false
    if (this.stackManager.length() === 0) return !!this.openValue
    return this.stackManager.top() === el
  }

  markDialogAsClosed(dialogEl) {
    dialogEl.setAttribute('data-state', 'closed')
  }

  removeFromStackAndRefresh(dialogEl) {
    this.stackManager.remove(dialogEl)
    this.refreshLayers()
  }

  setClosedStateOnElements(dialogEl) {
    this.cleanupService.markClosed(dialogEl)
  }
  disablePointerEvents(dialogEl) {
    this.cleanupService.disablePointerEvents(dialogEl)
  }

  // Aggressive cleanup routed through service to ensure consistent behavior
  cleanupAllDialogsForElement(el) {
    this.cleanupService.cleanupAllDialogsForElement(el, {
      animationDuration: CONSTANTS.ANIMATION_DURATION,
      onRefreshLayers: () => this.refreshLayers()
    })
  }


  // Aggressively clean up any dialog structures that contain the given element
  cleanupAllDialogsForElement(el) {
    this.cleanupService.cleanupAllDialogsForElement(el, {
      animationDuration: CONSTANTS.ANIMATION_DURATION,
      onRefreshLayers: () => this.refreshLayers()
    })
  }


  scheduleDialogCleanup(dialogEl) {
    this.cleanupService.scheduleCleanup(dialogEl, {
      animationDuration: CONSTANTS.ANIMATION_DURATION,
      onCleanupComplete: (el) => {
        try {
          this.cleanupDialog(el)
        } catch (error) {
          log.warn('Error during dialog cleanup:', error)
        }
        this.allowBodyScroll()
      }
    })
  }

  cleanupDialog(dialogEl) {
    if (this.insertedDialog === dialogEl) {
      this.cleanupInsertedDialogEvents()
      if (this.insertedDialog && this.insertedDialog.parentNode) {
        this.insertedDialog.remove()
      }
      this.insertedDialog = null
    } else {
      // Fallback: remove the dialog element directly
      if (dialogEl && dialogEl.parentNode) {
        dialogEl.remove()
      }
    }
  }

  purgeOrphanOverlays() {
    this.cleanupService.purgeOrphanOverlays({
      isActiveRootFn: (root, inStack) => {
        // Never purge active edit modals containing recipe forms
        return DialogDOMUtils.hasActiveRecipeForm(root)
      },
      animationDuration: CONSTANTS.ANIMATION_DURATION,
      onDisablePointerEvents: (el) => this.disablePointerEvents(el),
      onRefreshLayers: () => this.refreshLayers()
    })
  }

}
